import React, { useEffect, useMemo, useRef } from "react";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import TimerIcon from "@mui/icons-material/Timer";
import CircularProgress from "@mui/material/CircularProgress";
import TableTennisRacketIcon from "../TableTennisRacketIcon";
import { useBrand } from "../../context/BrandContext";
import { normalizeAppLang } from "../../utils/appLanguages";
import {
  localizedExerciseName,
  localizedTypeName,
} from "../../utils/localizedCatalogFields";
import { isPersonalizedExerciseType } from "../../utils/teExecutionType";
import "./SelectSessionTemplateDialog.css";

function sortedTemplateItems(template) {
  return (template?.template_items || [])
    .slice()
    .sort((a, b) => (a.tsti_order ?? 9999) - (b.tsti_order ?? 9999));
}

function blockLabel(item, lng, t) {
  if (isPersonalizedExerciseType(item?.type_ex || item?.tbl_type_ex)) {
    return t("personalizedExercise", "Personalized exercise");
  }
  const typeName = localizedTypeName(item?.type_ex, lng);
  if (typeName) return typeName;
  return localizedExerciseName(item?.base_exercise, lng) || null;
}

/**
 * Mobile-first sheet for picking a session template before apply.
 */
export function SelectSessionTemplateDialog({
  open,
  onClose,
  templates = [],
  selectedTemplate = null,
  onSelectTemplate,
  onApply,
  loading = false,
  applying = false,
  container,
}) {
  const { t, i18n } = useTranslation();
  const { brand } = useBrand();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const applyRef = useRef(null);
  const lng = normalizeAppLang(i18n.language);
  const brandPrimary = brand?.color_primary || "#0877BC";
  const brandSecondary = brand?.color_secondary || "#46528b";

  useEffect(() => {
    if (!open || !selectedTemplate) return;
    const id = window.setTimeout(() => applyRef.current?.focus?.(), 80);
    return () => window.clearTimeout(id);
  }, [open, selectedTemplate?.tst_id]);

  const countLabel = useMemo(
    () =>
      templates.length === 1
        ? t("selectTemplateCountOne") || "1 template"
        : t("selectTemplateCount", { count: templates.length }) ||
          `${templates.length} templates`,
    [t, templates.length]
  );

  const close = () => {
    if (applying) return;
    onClose?.();
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth
      className="sst-dialog"
      container={container || undefined}
      disableScrollLock={Boolean(container)}
      sx={container ? { zIndex: 5000 } : undefined}
      PaperProps={{
        className: `sst-dialog__paper${isMobile ? " sst-dialog__paper--mobile" : ""}`,
        style: {
          "--sst-brand": brandPrimary,
          "--sst-brand-secondary": brandSecondary,
        },
      }}
      aria-labelledby="sst-dialog-title"
    >
      <header className="sst-dialog__header">
        <div className="sst-dialog__header-copy">
          <p className="sst-dialog__kicker">
            {t("selectTemplateKicker") || "Session plan"}
          </p>
          <h2 id="sst-dialog-title" className="sst-dialog__title">
            {t("selectTemplate") || "Select Session Template"}
          </h2>
          {!loading && templates.length > 0 ? (
            <p className="sst-dialog__subtitle">{countLabel}</p>
          ) : null}
        </div>
        <IconButton
          className="sst-dialog__close"
          onClick={close}
          disabled={applying}
          aria-label={t("close") || "Close"}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </header>

      <div className="sst-dialog__body" role="listbox" aria-label={t("selectTemplate")}>
        {loading ? (
          <div className="sst-dialog__state" role="status">
            <CircularProgress size={28} thickness={4} sx={{ color: brandPrimary }} />
            <p>{t("loading") || "Loading…"}</p>
          </div>
        ) : templates.length === 0 ? (
          <div className="sst-dialog__state sst-dialog__state--empty">
            <TableTennisRacketIcon className="sst-dialog__empty-icon" />
            <p className="sst-dialog__empty-title">
              {t("noTemplatesAvailable") || "No templates available"}
            </p>
            <p className="sst-dialog__empty-hint">
              {t("selectTemplateEmptyHint") ||
                "Create a club template first, then apply it to this session."}
            </p>
          </div>
        ) : (
          <ul className="sst-dialog__list">
            {templates.map((template) => {
              const selected = selectedTemplate?.tst_id === template.tst_id;
              const items = sortedTemplateItems(template);
              const kind = String(template.tst_kind || "").toLowerCase();
              const preview = items.slice(0, 4);
              const extra = Math.max(0, items.length - preview.length);

              return (
                <li key={template.tst_id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={selected}
                    className={`sst-card${selected ? " is-selected" : ""}`}
                    disabled={applying}
                    onClick={() => onSelectTemplate?.(template)}
                  >
                    <div className="sst-card__top">
                      <div className="sst-card__titles">
                        <h3 className="sst-card__name">{template.tst_name}</h3>
                        {kind && kind !== "custom" ? (
                          <span className={`sst-card__kind sst-card__kind--${kind}`}>
                            {kind}
                          </span>
                        ) : null}
                      </div>
                      <span
                        className={`sst-card__check${selected ? " is-on" : ""}`}
                        aria-hidden
                      >
                        <CheckCircleIcon fontSize="inherit" />
                      </span>
                    </div>

                    {template.tst_description ? (
                      <p className="sst-card__desc">{template.tst_description}</p>
                    ) : null}

                    <div className="sst-card__metrics">
                      <span className="sst-metric" title={t("duration") || "Duration"}>
                        <TimerIcon className="sst-metric__icon" fontSize="inherit" />
                        <strong>
                          {template.tst_duration ?? "—"}
                        </strong>
                        <span>{t("min") || "min"}</span>
                      </span>
                      <span className="sst-metric" title={t("exercises") || "Exercises"}>
                        <TableTennisRacketIcon className="sst-metric__icon" fontSize="inherit" />
                        <strong>{items.length}</strong>
                        <span>
                          {items.length === 1
                            ? t("trainingBlockOne") || t("exercises") || "block"
                            : t("trainingBlocks") || t("exercises") || "blocks"}
                        </span>
                      </span>
                    </div>

                    {preview.length > 0 ? (
                      <ol className="sst-card__seq" aria-hidden={false}>
                        {preview.map((item, idx) => {
                          const personalized = isPersonalizedExerciseType(
                            item?.type_ex || item?.tbl_type_ex
                          );
                          const label = blockLabel(item, lng, t) || `Block ${idx + 1}`;
                          const mins = item.tsti_duration;
                          return (
                            <li key={item.tsti_id || `${template.tst_id}-${idx}`}>
                              <span className="sst-card__seq-n">{idx + 1}</span>
                              <span
                                className={`sst-card__seq-name${
                                  personalized ? " sst-card__seq-name--personalized" : ""
                                }`}
                              >
                                {label}
                              </span>
                              {mins != null && mins !== "" ? (
                                <span className="sst-card__seq-min">
                                  {mins}
                                  {t("min") || "min"}
                                </span>
                              ) : null}
                            </li>
                          );
                        })}
                        {extra > 0 ? (
                          <li className="sst-card__seq-more">
                            +{extra} {t("more") || "more"}
                          </li>
                        ) : null}
                      </ol>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <footer className="sst-dialog__footer">
        <button
          type="button"
          className="sst-dialog__btn sst-dialog__btn--ghost"
          onClick={close}
          disabled={applying}
        >
          {t("cancel") || "Cancel"}
        </button>
        <button
          ref={applyRef}
          type="button"
          id="apply-template-button"
          className="sst-dialog__btn sst-dialog__btn--primary"
          onClick={() => onApply?.()}
          disabled={!selectedTemplate || loading || applying}
        >
          {applying
            ? t("applying") || "Applying…"
            : t("applyTemplate") || "Apply Template"}
        </button>
      </footer>
    </Dialog>
  );
}

export default SelectSessionTemplateDialog;
